import { CircleOutlined, CircleRounded } from '@mui/icons-material';
import { Box, Divider, Grid, Stack, useTheme, SxProps, useMediaQuery } from '@mui/material';
import _ from 'lodash';
import React, { ReactNode, useEffect, useState } from 'react';
import useWindowDimensions from '../../../../../../app/hooks/useWindowDimensions';
import { drawerWidth } from '../../../../../../app/layouts/LAppOnline';
import IFundTypeHistoryPeriod from '../../../../interfaces/expertise/history/IFundTypeHistoryPeriod';
import TruncateMarkup from 'react-truncate-markup';
import styleVariables from '../../../../../../app/styles/styleVariables';
import CAppTooltip from '../../../../../../app/components/CAppTooltip';
import AppFormatAmount from '../../../../../../app/features/format/AppFormatAmount';
import investmentTypeHistoryLang from '../../langs/investmentTypeHistoryLang';
import { useTranslation } from 'react-i18next';
import AppLoaderGraph from '../../../../../../app/features/loader/graph/AppLoaderGraph';
import CAppPaper from '../../../../../../app/components/CAppPaper';
import investmentFundLang from '../../../../langs/investmentFundLang';
import { TInvestFundCurrency } from '../../../../types/fund/TInvestFundCurrency';
const height = 380;

type CHistoryTimelineProps = {
	historyPeriods: IFundTypeHistoryPeriod[] | null | undefined;
	btnPrevious: ReactNode;
	btnNext: ReactNode;
};
const CHistoryTimeline = (props: CHistoryTimelineProps) => {
	const { historyPeriods, btnPrevious, btnNext } = props;
	const { t } = useTranslation();

	return (
		<>
			{historyPeriods && historyPeriods.length > 0 ? (
				<Box sx={{ position: 'relative' }}>
					<Box sx={{ position: 'absolute', top: 'calc(50% - 32px)', left: '0px' }}>{btnPrevious}</Box>
					<Box sx={{ position: 'absolute', top: 'calc(50% - 32px)', right: '0px' }}>{btnNext}</Box>
					<Box
						sx={{
							height: `${height + 10}px`,
							marginLeft: '43px',
							marginRight: '30px',
							// overflowX: 'scroll',
							// display: 'flex',
							// flexWrap: 'wrap',
							// flexDirection: 'column',
						}}
					>
						<Stack id="expertise-history-timeline-component" direction="row" alignItems="center">
							{_.uniq(historyPeriods.map((x) => x.year)).map((year) => {
								return (
									<TimelineBox
										year={year}
										key={year}
										historyPeriods={historyPeriods.filter((p) => p.year === year)}
									/>
								);
							})}
						</Stack>
					</Box>
				</Box>
			) : historyPeriods && historyPeriods.length === 0 ? (
				<>
					<CAppPaper
						variant="outlined"
						sx={{
							textAlign: 'center',
							padding: '50px',
							width: '100%',
						}}
					>
						{t(investmentFundLang.text_no_data_available_yet.tag)}
					</CAppPaper>
				</>
			) : (
				<AppLoaderGraph height={height} />
			)}
		</>
	);
};

type TimelineBoxProps = {
	year: number;
	historyPeriods: IFundTypeHistoryPeriod[];
};
const TimelineBox = (props: TimelineBoxProps) => {
	const { year, historyPeriods } = props;
	const { width: innerWidth } = useWindowDimensions();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const ratioWidth = isMobile ? 1 : 3;
	const boxWidth = isMobile ? (innerWidth - 40) / ratioWidth : (innerWidth - drawerWidth - 40) / ratioWidth;
	return (
		<Box
			sx={{
				position: 'relative',
				width: boxWidth + 'px',
				height: `${height}px`,
			}}
		>
			<Box
				className="timeline-box-year"
				sx={{
					position: 'absolute',
					width: '100%',
					top: '0px',
					left: '0px',
					opacity: 1,
					textAlign: 'center',
					fontSize: isMobile ? '120px' : '160px',
					lineHeight: `${height}px`,
					fontWeight: 700,
					color: theme.palette.background.default,
					fontFamily: 'Cambo',
					WebkitFontSmoothing: 'subpixel-antialiased',
					textShadow: theme.palette.mode === 'dark' ? '0 0 1px #FFF' : '0 0 1px #070707ab',
				}}
			>
				{year}
			</Box>
			<Box
				sx={{
					position: 'absolute',
					width: '100%',
					height: '2px',
					top: `${height / 2}px`,
					left: '0px',
					opacity: 1,
					textAlign: 'center',
					fontSize: '20px',
				}}
			>
				<Divider sx={{ backgroundColor: theme.palette.secondary.main, width: '100%', height: '2px' }} />
			</Box>
			<Box
				sx={{
					position: 'absolute',
					width: '100%',
					height: `${height}px`,
					top: '0px',
					left: '0px',
					opacity: 1,
					textAlign: 'center',
					fontSize: '20px',
					color: '#E7E7E7',
				}}
			>
				<Grid container spacing={0} alignItems="center" justifyContent="center" sx={{ height: `${height}px` }}>
					<Grid item xs={3}>
						<QuarterBox position="bottom" historyPeriod={historyPeriods.find((x) => x.quarter === 1)} />
					</Grid>
					<Grid item xs={3}>
						<QuarterBox position="top" historyPeriod={historyPeriods.find((x) => x.quarter === 2)} />
					</Grid>
					<Grid item xs={3}>
						<QuarterBox position="bottom" historyPeriod={historyPeriods.find((x) => x.quarter === 3)} />
					</Grid>
					<Grid item xs={3}>
						<QuarterBox position="top" historyPeriod={historyPeriods.find((x) => x.quarter === 4)} />
					</Grid>
				</Grid>
			</Box>
		</Box>
	);
};
type QuarterBoxProps = {
	position: 'top' | 'bottom';
	historyPeriod: IFundTypeHistoryPeriod | undefined;
};
const QuarterBox = (props: QuarterBoxProps) => {
	const { historyPeriod, position } = props;
	const title = historyPeriod
		? !historyPeriod.funds || historyPeriod.funds.length === 0
			? null
			: historyPeriod.name
		: null;
	const theme = useTheme();
	return (
		<Box sx={{ textAlign: 'center' }}>
			<Box
				sx={{
					height: `${(height - 70 * 2 - 20) / 2}px`,
					textAlign: 'center',
					margin: 'auto',
					fontSize: '1rem',
					padding: '5px',
					color: theme.palette.primary.main,
					position: 'relative',
				}}
			>
				{position === 'top' ? (
					<QuarterBoxText
						sx={{ position: 'absolute', bottom: 10, left: 0, width: '100%' }}
						historyPeriod={historyPeriod}
					/>
				) : (
					''
				)}
			</Box>
			<Box
				sx={{
					height: '70px',
					backgroundColor: theme.palette.secondary.main,
					width: position === 'top' && title ? '1px' : '0px',
					margin: 'auto',
					marginBottom: '2px',
				}}
			/>
			<Box
				sx={{
					position: 'relative',
					lineHeight: '20px',
					color: title ? theme.palette.secondary.main : '#F2F2F2',
				}}
			>
				{title ? (
					<CircleRounded
						sx={{
							height: '20px',
							width: '20px',
							border: 'solid',
							borderWidth: theme.palette.mode === 'dark' ? '1px' : '0px',
							borderRadius: '10px',
							borderColor: theme.palette.mode === 'dark' ? '#FFF' : theme.palette.secondary.main,
						}}
					/>
				) : (
					<>
						<Box
							sx={{
								position: 'absolute',
								top: '8px',
								left: '0px',
								textAlign: 'center',
								width: '100%',
								height: '2px',
							}}
						>
							<Box
								sx={{
									margin: 'auto',
									backgroundColor: theme.palette.background.default,
									width: '13px',
									height: '2px',
								}}
							/>
						</Box>
						<CircleOutlined sx={{ height: '20px', width: '20px', color: theme.palette.secondary.main }} />
					</>
				)}
			</Box>

			<Box
				sx={{
					height: '70px',
					backgroundColor: theme.palette.secondary.main,
					width: position === 'bottom' && title ? '1px' : '0px',
					margin: 'auto',
					marginTop: '2px',
				}}
			/>
			<Box
				sx={{
					height: `${(height - 70 * 2 - 20) / 2}px`,
					position: 'relative',
					textAlign: 'center',
					margin: 'auto',
					fontSize: '1rem',
					padding: '5px',
					color: theme.palette.primary.main,
				}}
			>
				{position === 'bottom' ? (
					<QuarterBoxText
						sx={{ position: 'absolute', top: 10, left: 0, width: '100%' }}
						historyPeriod={historyPeriod}
					/>
				) : (
					''
				)}
			</Box>
		</Box>
	);
};

type QuarterBoxTextProps = {
	historyPeriod: IFundTypeHistoryPeriod | undefined;
	sx?: SxProps;
};
const QuarterBoxText = (props: QuarterBoxTextProps) => {
	const { historyPeriod, sx } = props;

	return (
		<CAppTooltip title={<QuarterBoxTooltip historyPeriod={historyPeriod} />} placement="right">
			<Box sx={{ ...sx }}>
				{historyPeriod && historyPeriod.funds && historyPeriod.funds.length > 0 ? (
					<>
						<Box
							className="quarter-box-text"
							sx={{
								fontWeight: 700,
								fontSize: '1rem',
							}}
						>
							Q{historyPeriod.quarter}
						</Box>
						<Box sx={{ fontWeight: 'normal', fontSize: '0.75rem' }}>
							{historyPeriod.funds.slice(0, 2).map((fund) => {
								return (
									<Box
										key={fund.fundId}
										sx={{
											color: fund.fundIsRealized ? styleVariables.greyColor : '',
										}}
									>
										<TruncateMarkup lines={1}>
											<Box>{fund?.fundName}</Box>
										</TruncateMarkup>
									</Box>
								);
							})}
						</Box>
						{historyPeriod.funds.length > 2 ? (
							<Box
								sx={{
									fontWeight: 600,
									fontSize: '0.75rem',
									textDecoration: 'underline',
								}}
							>
								+{historyPeriod.funds.length - 2} funds
							</Box>
						) : (
							''
						)}
					</>
				) : (
					''
				)}
			</Box>
		</CAppTooltip>
	);
};

type QuarterBoxTooltipProps = {
	historyPeriod: IFundTypeHistoryPeriod | undefined;
};
const QuarterBoxTooltip = (props: QuarterBoxTooltipProps) => {
	const { historyPeriod } = props;

	const { t } = useTranslation();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const [fundWithCapitalCalls, setFundWithCapitalCalls] = useState<IFundTypeHistoryPeriod['funds'] | null>(null);
	const [fundWithCommitments, setFundWithCommitments] = useState<IFundTypeHistoryPeriod['funds'] | null>(null);

	useEffect(() => {
		if (historyPeriod) {
			setFundWithCapitalCalls(historyPeriod.funds.filter((x) => x.calledCapital && x.calledCapital !== 0));
			setFundWithCommitments(historyPeriod.funds.filter((x) => x.commitments && x.commitments !== 0));
		}
	}, [historyPeriod]);

	return (
		<Box
			sx={{
				width: isMobile ? '100%' : '240px',
				padding: '5px',
				'& .title': {
					fontSize: '0.85rem',
					fontWeight: 600,
					lineHeight: '1.5',
				},
				'& .box': {
					margin: '11px 0px',
				},
				'& .subtitle': {
					fontWeight: 500,
					fontSize: '0.85rem',
					lineHeight: '1.5',
					letterSpacing: '2px',
					marginBottom: '5px',
				},
				'& .list': {
					fontSize: '0.85rem',
					marginBottom: '7px',
					fontWeight: 300,
					lineHeight: '1.1',
					'&.realized': {
						color: styleVariables.greyColor,
					},
				},
			}}
		>
			<Box className="title">{historyPeriod?.name}</Box>
			{fundWithCapitalCalls && fundWithCapitalCalls.length > 0 ? (
				<>
					<Box className="box">
						<Box className="subtitle">{t(investmentTypeHistoryLang.timeline_capital_calls.tag)}</Box>

						{fundWithCapitalCalls.map((x, index) => {
							return (
								<QuarterBoxTooltipLine
									key={index}
									label={x.fundName}
									amount={x.calledCapital}
									isRealized={x.fundIsRealized}
									currency={x.currency}
								/>
							);
						})}
					</Box>
				</>
			) : (
				''
			)}
			{fundWithCapitalCalls &&
			fundWithCapitalCalls.length > 0 &&
			fundWithCommitments &&
			fundWithCommitments.length > 0 ? (
				<Divider sx={{ backgroundColor: '#FFF' }} />
			) : (
				''
			)}
			{fundWithCommitments && fundWithCommitments.length > 0 ? (
				<Box className="box">
					<Box className="subtitle">{t(investmentTypeHistoryLang.timeline_commitments.tag)}</Box>
					{fundWithCommitments.map((x, index) => {
						return (
							<QuarterBoxTooltipLine
								key={index}
								label={x.fundName}
								amount={x.commitments}
								isRealized={x.fundIsRealized}
								currency={x.currency}
							/>
						);
					})}
				</Box>
			) : (
				''
			)}
		</Box>
	);
};

type QuarterBoxTooltipLineProps = {
	label: string;
	amount: number;
	isRealized: boolean;
	currency: TInvestFundCurrency;
};
const QuarterBoxTooltipLine = (props: QuarterBoxTooltipLineProps) => {
	const { label, amount, isRealized, currency } = props;
	return (
		<Grid
			container
			className={`list ${isRealized ? 'realized' : ''}`}
			spacing={1}
			sx={{ width: '100%' }}
			alignItems="center"
			justifyContent="center"
		>
			<Grid item xs={8}>
				{label}
			</Grid>
			<Grid item xs={4} sx={{ textAlign: 'right' }}>
				<AppFormatAmount value={amount} addSpace currency={currency} />
			</Grid>
		</Grid>
	);
};

export default CHistoryTimeline;
